import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Breadcrumbs,
  Link,
  Tooltip,
  ButtonGroup
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Send as SendIcon,
  GetApp as DownloadIcon,
  SwapHoriz as ConvertIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  CheckCircle as AcceptIcon,
  Cancel as RejectIcon,
  HourglassEmpty as ExpireIcon
} from '@mui/icons-material';
import { EstimateResponse, EstimateStatus } from '../../types/estimate';
import { DocumentContextMenu } from '../documents/DocumentContextMenu';

interface EstimateHeaderProps {
  estimate: EstimateResponse;
  permissions: {
    canEdit: boolean;
    canSend: boolean;
    canConvert: boolean;
    canDelete: boolean;
    canAccept: boolean;
    canReject: boolean;
    canExpire: boolean;
  };
  actionLoading: boolean;
  onEdit: () => void;
  onSend: () => void;
  onDownloadPDF: () => void;
  onConvert: () => void;
  onDelete: () => void;
  onAccept: () => void;
  onReject: () => void;
  onExpire: () => void;
}

const EstimateHeader: React.FC<EstimateHeaderProps> = ({
  estimate,
  permissions,
  actionLoading,
  onEdit,
  onSend,
  onDownloadPDF,
  onConvert,
  onDelete,
  onAccept,
  onReject,
  onExpire
}) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const getStatusColor = (status: EstimateStatus) => {
    switch (status) {
      case EstimateStatus.DRAFT:
        return 'default';
      case EstimateStatus.SENT:
        return 'info';
      case EstimateStatus.ACCEPTED:
        return 'success';
      case EstimateStatus.REJECTED:
        return 'error';
      case EstimateStatus.EXPIRED:
        return 'warning';
      case EstimateStatus.CONVERTED:
        return 'secondary';
      default:
        return 'default';
    }
  };

  return (
    <>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link color="inherit" href="/admin/dashboard">
          Dashboard
        </Link>
        <Link color="inherit" href="/admin/documents/estimates">
          Estimates
        </Link>
        <Typography color="text.primary">
          {estimate.estimate_number}
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate('/admin/documents/estimates')}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4">
              Estimate {estimate.estimate_number}
            </Typography>
            <Box display="flex" alignItems="center" gap={2} mt={1}>
              <Chip 
                label={estimate.status.toUpperCase()} 
                color={getStatusColor(estimate.status)}
                size="small"
                sx={{ fontWeight: 600 }}
              />
              {estimate.status === EstimateStatus.DRAFT && (
                <Chip 
                  label="Ready to Send" 
                  size="small"
                  variant="outlined"
                  color="info"
                  sx={{ fontWeight: 500 }}
                />
              )}
              {estimate.status === EstimateStatus.SENT && (
                <Chip 
                  label="Awaiting Customer Response" 
                  size="small"
                  variant="outlined"
                  color="info"
                  sx={{ fontWeight: 500 }}
                />
              )}
              {estimate.status === EstimateStatus.ACCEPTED && !estimate.converted_to_order_id && (
                <Chip 
                  label="Ready to Convert" 
                  size="small"
                  variant="outlined"
                  color="success"
                  sx={{ fontWeight: 500 }}
                />
              )}
              {estimate.converted_to_order_id && (
                <Tooltip title={`Converted to Sales Order on ${estimate.converted_at ? new Date(estimate.converted_at).toLocaleDateString() : 'N/A'}`}>
                  <Chip 
                    label={`CONVERTED TO ORDER`}
                    color="secondary"
                    size="small"
                    clickable
                    onClick={() => navigate(`/admin/documents/sales-orders/${estimate.converted_to_order_id}`)}
                    sx={{ fontWeight: 600 }}
                  />
                </Tooltip>
              )}
            </Box>
          </Box>
        </Box>
        
        <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
          {/* Status-specific action buttons */}
          {estimate.status === EstimateStatus.DRAFT && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<SendIcon />}
              onClick={onSend}
              disabled={actionLoading}
              sx={{ 
                textTransform: 'none', 
                fontWeight: 600,
                boxShadow: '0 2px 8px rgba(33, 150, 243, 0.3)'
              }}
            >
              Send to Customer
            </Button>
          )}
          
          {/* Status Transition Buttons - Show for DRAFT and SENT statuses */}
          {(estimate.status === EstimateStatus.DRAFT || estimate.status === EstimateStatus.SENT) && (
            <ButtonGroup variant="contained" size="medium" sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
              <Tooltip title="Mark as Accepted by Customer">
                <Button
                  startIcon={<AcceptIcon />}
                  onClick={onAccept}
                  disabled={actionLoading}
                  color="success"
                  sx={{ textTransform: 'none', fontWeight: 500 }}
                >
                  Accept
                </Button>
              </Tooltip>
              <Tooltip title="Mark as Rejected by Customer">
                <Button
                  startIcon={<RejectIcon />}
                  onClick={onReject}
                  disabled={actionLoading}
                  color="error"
                  sx={{ textTransform: 'none', fontWeight: 500 }}
                >
                  Reject
                </Button>
              </Tooltip>
              <Tooltip title="Mark as Expired">
                <Button
                  startIcon={<ExpireIcon />}
                  onClick={onExpire}
                  disabled={actionLoading}
                  color="warning"
                  sx={{ textTransform: 'none', fontWeight: 500 }}
                >
                  Expire
                </Button>
              </Tooltip>
            </ButtonGroup>
          )}
          
          {/* Convert to Order - Only show when ACCEPTED */}
          {permissions.canConvert && (
            <Button
              variant="contained"
              color="secondary"
              startIcon={<ConvertIcon />}
              onClick={onConvert}
              disabled={actionLoading}
              sx={{ 
                textTransform: 'none', 
                fontWeight: 600,
                boxShadow: '0 2px 8px rgba(244, 67, 54, 0.3)'
              }}
            >
              Convert to Order
            </Button>
          )}
          
          {/* Secondary Actions */}
          {permissions.canEdit && (
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={onEdit}
              disabled={actionLoading}
              sx={{ textTransform: 'none', fontWeight: 500 }}
            >
              Edit
            </Button>
          )}
          
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={onDownloadPDF}
            disabled={actionLoading}
            sx={{ textTransform: 'none', fontWeight: 500 }}
          >
            Download PDF
          </Button>
          
          <DocumentContextMenu
            documentId={estimate.id}
            documentType="estimate"
            documentNumber={estimate.estimate_number}
            customerEmail={estimate.customer_email}
            customerPhone={estimate.customer_mobile}
          />
          
          <IconButton onClick={handleMenuClick}>
            <MoreVertIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {permissions.canDelete && (
          <MenuItem onClick={() => { onDelete(); handleMenuClose(); }}>
            <DeleteIcon sx={{ mr: 1 }} />
            Delete
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default EstimateHeader;